/**
 * Which client the app uses.
 *
 * LIVE IS THE DEFAULT AND A PRODUCTION BUILD CONTAINS NOTHING ELSE.
 *
 * The fixture module is reached through a DYNAMIC import behind an
 * `import.meta.env.DEV` guard, and that shape is deliberate. A static import
 * — even one whose only use sat in a dead branch — kept the whole fixture
 * module in the production bundle: `camera_truck`, the fixture sha256 strings
 * and the seeded productions all shipped. Unreachable, but present, and
 * present is the state that can be reached by accident.
 *
 * With the guard, `import.meta.env.DEV` is replaced by `false` at build time,
 * the function returns before the `import()`, and Rollup drops the chunk
 * entirely. Verified by grepping the built artefact, not assumed.
 */

import { liveService } from './live';
import type { ModellerService } from './service';

let active: ModellerService = liveService;

/** Delegates, so the binding components import never goes stale. */
export const modeller: ModellerService = {
  get mode() { return active.mode; },
  productions: () => active.productions(),
  production: (slug) => active.production(slug),
  equipment: () => active.equipment(),
  estimates: (slug) => active.estimates(slug),
  estimate: (id) => active.estimate(id),
  daySuggestions: (id) => active.daySuggestions(id),
  createProduction: (body) => active.createProduction(body),
  documents: (slug) => active.documents(slug),
  documentExtractions: (slug, id) => active.documentExtractions(slug, id),
  createShootDay: (slug, body) => active.createShootDay(slug, body),
  registerDocument: (slug, body) => active.registerDocument(slug, body),
  extractCallSheet: (slug, id, file, by) => active.extractCallSheet(slug, id, file, by),
  confirmFact: (factId, body) => active.confirmFact(factId, body),
};

/** True only in a dev build, and only when explicitly asked for. */
export function fixturesRequested(): boolean {
  if (!import.meta.env.DEV) return false;
  if (import.meta.env.VITE_MODELLER_FIXTURES === '1') return true;
  return typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).has('fixtures');
}

/**
 * Swap in the fixture client. A no-op in a production build, and the early
 * return is what lets the bundler remove the import below it.
 */
export async function enableFixtures(): Promise<void> {
  if (!import.meta.env.DEV) return;
  const { fixtureService } = await import('../fixtures/client');
  active = fixtureService;
}

export * from './models';
export { NotAvailable, FixtureWriteRefused } from './service';
export type { ModellerService } from './service';
